import { Check } from 'lucide-react';
import Indeterminate from '@/icons/minus.svg?react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as React from 'react';

import { cn } from '@/helpers/ui-utils';
import { cva, VariantProps } from 'class-variance-authority';
import { Label } from '../';
import HelperText from '../helperText';

const checkboxVariants = cva(
  [
    'group',
    'peer',
    'h-6',
    'w-6',
    'p-0',
    'shrink-0',
    'border',
    'border-solid',
    'border-[#D4D4D4]',
    'bg-background',
    'data-[state=checked]:bg-primary',
    'data-[state=checked]:border-primary',
    'data-[state=indeterminate]:bg-primary',
    'data-[state=indeterminate]:border-primary',
    'transition-[shadow_border]',
    'duration-300',
    'ease-in-out',
    'hover:cursor-pointer',
    'hover:border-[#A3A3A3]',
    'hover:data-[state=checked]:bg-[#E57748]',
    'hover:data-[state=indeterminate]:bg-[#E57748]',
    'focus:outline-0',
    'focus:ring-3',
    'focus:ring-offset-0',
    'focus:ring-[#D4D4D4]',
    'focus:border-[#A3A3A3]',
    'focus:data-[state=checked]:ring-[#E6ECFF]',
    'focus:data-[state=indeterminate]:ring-[#E6ECFF]',
  ],
  {
    variants: {
      variant: {
        default: ['rounded-[6px]'],
        rounded: ['rounded-full'],
      },
      disabled: {
        true: ['cursor-not-allowed', 'opacity-50', 'hover:border-input'],
      },
      invalid: {
        true: [
          'border-warning',
          'shadow-none',
          'hover:border-warning',
          'hover:shadow-none',
          'focus:border-warning',
          'focus:shadow-none',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
      disabled: false,
      invalid: false,
    },
  },
);

type CheckboxCVAProps = {
  /**
   * Visual style variant of the checkbox. Determines the shape and appearance.
   * - `default` - Square checkbox with rounded corners
   * - `rounded` - Circular checkbox (radio-button style)
   * @default 'default'
   */
  variant?: VariantProps<typeof checkboxVariants>['variant'];

  /**
   * Indicates if the checkbox is in a disabled state. Prevents user interaction.
   * @default false
   */
  disabled?: VariantProps<typeof checkboxVariants>['disabled'];

  /**
   * Indicates if the checkbox is in an invalid/error state. Applies warning styling.
   * @default false
   */
  invalid?: VariantProps<typeof checkboxVariants>['invalid'];
};

export interface CheckboxProps extends CheckboxCVAProps {
  /**
   * The name attribute for the checkbox input element.
   */
  name: string;

  /**
   * The label content to display next to the checkbox. Can be text, JSX elements, or components.
   * This is required for accessibility and user understanding.
   */
  label: React.ReactNode;

  /**
   * Optional helper text displayed below the checkbox and label.
   * Provides additional context, instructions, or validation feedback.
   */
  helperText?: string;
}

/**
 * Enable single or multiple selections with accessible checkboxes that support indeterminate states and custom validation.
 *
 * @param props - Checkbox properties. See {@link CheckboxProps} for detailed prop documentation including inherited CVA variants.
 * @param ref - Forwarded ref to the checkbox element
 * @returns Rendered checkbox component
 *
 * @example
 * ```tsx
 * // Basic checkbox
 * <Checkbox label="Accept terms and conditions" />
 *
 * // Checkbox with helper text
 * <Checkbox
 *   label="Subscribe to newsletter"
 *   helperText="You can unsubscribe at any time"
 * />
 *
 * // Circular checkbox variant
 * <Checkbox variant="rounded" label="Select option" />
 *
 * // Invalid state with error message
 * <Checkbox
 *   label="Required field"
 *   invalid
 *   helperText="This field is required"
 * />
 *
 * // Controlled checkbox
 * <Checkbox
 *   label="Controlled"
 *   checked={isChecked}
 *   onCheckedChange={setIsChecked}
 * />
 * ```
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & CheckboxProps
>(
  (
    {
      variant = 'default',
      className,
      invalid = false,
      label,
      helperText,
      name,
      ...props
    },
    ref,
  ) => {
    return (
      <div className="flex w-full flex-col gap-1">
        <Label className="flex items-center gap-2">
          <CheckboxPrimitive.Root
            name={name}
            ref={ref}
            aria-invalid={invalid || false}
            className={cn(
              checkboxVariants({
                variant,
                invalid,
                disabled: props.disabled,
              }),
              className,
            )}
            {...props}
          >
            <CheckboxPrimitive.Indicator
              forceMount
              className={cn([
                'flex',
                'items-center',
                'justify-center',
                'text-current',
                'opacity-0',
                'group-data-[state=checked]:opacity-100',
                'group-data-[state=indeterminate]:opacity-100',
              ])}
            >
              {props.checked === 'indeterminate' ? (
                <Indeterminate className="h-4 w-4" />
              ) : (
                <Check className="h-5 w-5 [&_path]:stroke-white" />
              )}
            </CheckboxPrimitive.Indicator>
          </CheckboxPrimitive.Root>
          <span
            className={cn(
              ['peer-disabled:opacity-50', 'hover:cursor-pointer'],
              {
                'hover:cursor-not-allowed': props.disabled,
              },
            )}
          >
            {label}
          </span>
        </Label>
        {helperText && (
          <HelperText id={name} invalid={invalid === true} className="pl-8">
            {helperText}
          </HelperText>
        )}
      </div>
    );
  },
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
